import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, ArrowRight, CheckCircle2, Layers, BarChart3, Wifi, MapPinned, Building, Briefcase, Clock, GraduationCap } from "lucide-react";
import { ICONS } from "@/data/content";

export function Hero() {
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (loc.trim()) p.set("location", loc.trim());
    nav(`/jobs?${p.toString()}`);
  };
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-[#f3fbf6]" data-testid="hero-section">
      <div className="container-x grid items-center gap-10 py-10 lg:grid-cols-[1.1fr_1fr] lg:py-20">
        <div className="fade-up">
          <span className="eyebrow">More Than a Job Portal</span>
          <h1 className="mt-5 text-[2.1rem] font-extrabold leading-[1.08] tracking-tight text-[#0B1B2B] sm:text-5xl lg:text-6xl">
            Every Career. Every Industry.<br /><span className="text-[#00B94F]">One Platform.</span>
          </h1>
          <p className="mt-5 max-w-lg text-base text-slate-600 sm:text-lg">Discover opportunities across India—from your first job to your next big move.</p>
          <form onSubmit={submit} className="mt-8 flex flex-col gap-2 rounded-xl border border-[#e3ece6] bg-white p-2 shadow-[0_18px_40px_-20px_rgba(11,27,43,0.25)] sm:flex-row" data-testid="hero-search-form">
            <label className="flex flex-1 items-center gap-2 px-3"><Search size={18} className="text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Job title, skills or company" className="w-full py-2.5 text-sm outline-none" data-testid="hero-keyword-input" />
            </label>
            <label className="flex flex-1 items-center gap-2 border-t px-3 sm:border-l sm:border-t-0"><MapPin size={18} className="text-slate-400" />
              <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="City or Remote" className="w-full py-2.5 text-sm outline-none" data-testid="hero-location-input" />
            </label>
            <button className="btn-green" data-testid="hero-search-btn">Search Jobs <ArrowRight size={16} /></button>
          </form>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            {["Free to join", "All experience levels", "Pan-India opportunities"].map((t) => <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-[#00B94F]" />{t}</span>)}
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-md fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="absolute -right-6 -top-6 h-64 w-64 rounded-full bg-[#00B94F]/15 blur-2xl" />
          <img src="/images/hero.webp" srcSet="/images/hero-480.webp 480w, /images/hero.webp 800w" sizes="(max-width: 640px) 90vw, 448px" width="800" height="1200" fetchPriority="high" decoding="async" alt="Young professional searching for jobs on a laptop" className="relative h-72 w-full rounded-3xl object-cover object-top shadow-2xl sm:h-[460px]" />
          <div className="absolute -left-6 top-8 hidden rounded-xl bg-[#0B1B2B] p-4 text-white shadow-xl sm:block">
            <p className="text-sm font-bold">Find <span className="text-[#00B94F]">Better Jobs</span></p>
            <ul className="mt-3 space-y-2 text-xs text-white/80">
              {[[Wifi, "Remote Jobs"], [Clock, "Full-time"], [GraduationCap, "Internships"], [Briefcase, "Part-time"]].map(([I, t]) => <li key={t} className="flex items-center gap-2"><I size={13} className="text-[#00B94F]" />{t}</li>)}
            </ul>
          </div>
          <div className="absolute -bottom-5 right-4 rounded-xl border border-[#e3ece6] bg-white p-4 shadow-xl">
            <p className="text-xs font-semibold">Marketing Executive</p>
            <p className="text-[11px] text-slate-500">₹20,000 – 30,000 · Noida · Full-time</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ValueStrip() {
  const items = [[Layers, "All Industries", "From IT to hospitality"], [BarChart3, "Freshers to Experienced", "Opportunities for every career stage"], [MapPinned, "Remote & On-site", "Work from anywhere in India"], [Building, "Pan-India", "Connecting talent nationwide"]];
  return (
    <section className="border-y border-[#d9f0e1] bg-[#EAF8EF]" data-testid="value-strip">
      <div className="container-x grid grid-cols-2 gap-4 py-6 sm:gap-6 lg:grid-cols-4">
        {items.map(([I, t, s]) => (
          <div key={t} className="flex items-start gap-2.5 sm:items-center sm:gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white text-[#00B94F] sm:h-10 sm:w-10"><I size={18} /></span>
            <div><p className="text-[13px] font-semibold leading-tight sm:text-sm">{t}</p><p className="mt-0.5 text-[11px] text-slate-500 sm:text-xs">{s}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Categories({ categories }) {
  return (
    <section className="container-x py-12 sm:py-16" data-testid="categories-section">
      <div className="text-center">
        <h2 className="h-section">Find the right opportunity for you</h2>
        <p className="mt-2 text-sm text-slate-500">Explore jobs across top categories</p>
      </div>
      <div className="mt-8 grid grid-cols-3 gap-2.5 sm:mt-10 sm:gap-4 lg:grid-cols-6">
        {categories.map((c) => {
          const I = ICONS[c.icon] || Briefcase;
          return (
            <Link key={c.slug} to={`/categories/${c.slug}`} title={`${c.name} jobs in India`} className="card-hover group flex flex-col items-center px-2 py-4 text-center sm:px-3 sm:py-6" data-testid={`category-${c.slug}`}>
              <I size={26} className="text-[#00B94F] transition-transform group-hover:scale-110" />
              <p className="mt-2 text-xs font-semibold leading-tight sm:mt-3 sm:text-sm">{c.name}</p>
              <span className="mt-2 hidden items-center gap-1 text-xs font-medium text-[#009a41] sm:flex">Explore jobs <ArrowRight size={12} /></span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
